import { useState, useEffect, useRef } from "react";
import { useAuth, useLanguage, API } from "../App";
import { useNavigate } from "react-router-dom";
import { Search, X, FolderKanban, CheckSquare, Layers, AlertTriangle } from "lucide-react";

const TYPE_ICONS = {
  project: { icon: FolderKanban, color: "text-blue-500", bg: "bg-blue-50" },
  task: { icon: CheckSquare, color: "text-green-500", bg: "bg-green-50" },
  drawing: { icon: Layers, color: "text-purple-500", bg: "bg-purple-50" },
  incident: { icon: AlertTriangle, color: "text-red-500", bg: "bg-red-50" },
};

const TYPE_LABELS = { project: "Projekt", task: "Uppgift", drawing: "Ritning", incident: "Avvikelse" };

export const SearchBar = () => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);
  const sv = (s, e) => language === "sv" ? s : e;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const search = async (q) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` }, credentials: "include"
      });
      if (res.ok) {
        const d = await res.json();
        setResults(d.results);
        setOpen(true);
      }
    } catch {}
    finally { setLoading(false); }
  };

  const handleSelect = (result) => {
    setOpen(false);
    setQuery("");
    navigate(result.link);
  };

  const clear = () => { setQuery(""); setResults([]); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={sv("Sök projekt, uppgifter, ritningar...", "Search projects, tasks, drawings...")}
          className="w-full sm:w-64 lg:w-80 h-9 pl-9 pr-8 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors"
          data-testid="global-search-input"
        />
        {query && (
          <button onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-200">
            <X className="w-3.5 h-3.5 text-slate-400" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute top-full mt-1 w-full sm:w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="search-results-dropdown">
          {loading ? (
            <div className="py-6 text-center"><div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : results.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-400">
              {sv("Inga resultat", "No results")}
            </div>
          ) : (
            <div className="max-h-[350px] overflow-y-auto py-1">
              {results.map((r, i) => {
                const cfg = TYPE_ICONS[r.type] || TYPE_ICONS.project;
                const Icon = cfg.icon;
                return (
                  <button
                    key={`${r.type}-${r.id}-${i}`}
                    onClick={() => handleSelect(r)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`search-result-${i}`}
                  >
                    <div className={`p-1.5 rounded-md ${cfg.bg}`}>
                      <Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{r.title}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-slate-400 uppercase">{TYPE_LABELS[r.type] || r.type}</span>
                        {r.subtitle && <span className="text-[10px] text-slate-400">{r.subtitle}</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
